import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { motion } from 'framer-motion';

export default function ManagementTab() {
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState('');
  const [onlyOverdue, setOnlyOverdue] = useState(false);

  useEffect(() => {
    api.get('/borrowings')
      .then(res => setRecords(Array.isArray(res.data) ? res.data : (res.data?.borrowings || [])))
      .catch(() => alert('❌ فشل في تحميل سجلات الاستعارة'));
  }, []);

  const getBg = (due) => {
    const now = new Date();
    const d = new Date(due);
    const diffHours = (d - now) / (1000 * 60 * 60);
    if (diffHours < 0) return 'bg-red-100 text-red-700 font-semibold';        // متأخر
    if (diffHours <= 24) return 'bg-yellow-100 text-yellow-800 font-medium';  // قريب
    return 'bg-green-100 text-green-800';                                     // بعيد
  };

  const fmtDate = (v) => {
    if (!v) return '—';
    try { return new Date(v).toLocaleDateString(); } catch { return String(v); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter((r) => {
      const userName = String(r.user?.name || '').toLowerCase();
      const bookName = String(r.book?.name || '').toLowerCase();
      const match = !q || userName.includes(q) || bookName.includes(q);
      if (!match) return false;
      if (!onlyOverdue) return true;
      // فقط المتأخرين
      return new Date(r.dueDate) < new Date();
    });
  }, [records, search, onlyOverdue]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="container mx-auto max-w-7xl px-4 pt-4"
      dir="rtl"
    >
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">📋 إدارة الاستعارات</h2>

      {/* شريط أدوات */}
      <div className="bg-white border rounded-xl shadow p-4 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔎 بحث بالاسم أو الكتاب..."
          className="border rounded p-2 w-full sm:max-w-sm"
        />
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={onlyOverdue}
            onChange={(e) => setOnlyOverdue(e.target.checked)}
            className="accent-blue-600 w-4 h-4"
          />
          إظهار المتأخرين فقط
        </label>
      </div>

      {/* نسخة البطاقات (موبايل) */}
      <div className="grid gap-4 sm:hidden">
        {filtered.length === 0 && (
          <div className="text-center text-gray-500">لا توجد سجلات مطابقة.</div>
        )}
        {filtered.map((r) => {
          const phone = r.user?.phone?.replace(/[^0-9]/g, '') || '';
          const msg = encodeURIComponent(`📚 الرجاء إرجاع الكتاب "${r.book?.name}" قبل ${fmtDate(r.dueDate)}`);
          return (
            <div key={r._id} className="bg-white rounded-xl border shadow p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-blue-900">{r.user?.name || '—'}</h3>
                <span className={`text-xs px-2 py-1 rounded ${getBg(r.dueDate)}`}>
                  {fmtDate(r.dueDate)}
                </span>
              </div>
              <p className="text-gray-800 mt-1">📖 {r.book?.name || '—'}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm">
                  📣 {r.notificationSent ? 'تم الإشعار ✅' : 'لم يُشعَر ❌'}
                </span>
                {phone ? (
                  <a
                    href={`https://wa.me/${phone}?text=${msg}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 rounded-full bg-emerald-600 text-white text-sm hover:bg-emerald-700 transition"
                  >
                    إرسال واتساب
                  </a>
                ) : (
                  <span className="text-gray-400 italic text-sm">لا يوجد رقم</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* نسخة الجدول (شاشات متوسطة فما فوق) */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto rounded-xl shadow border border-gray-300 bg-white">
          <table className="min-w-full text-sm text-right">
            <thead className="bg-gray-100 text-gray-700 text-md sticky top-0">
              <tr>
                <th className="border px-4 py-3">👤 المستخدم</th>
                <th className="border px-4 py-3">📘 الكتاب</th>
                <th className="border px-4 py-3">⏰ تاريخ الإرجاع</th>
                <th className="border px-4 py-3 text-center">📣 تم الإشعار</th>
                <th className="border px-4 py-3 text-center">📨 إجراء</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                    لا توجد سجلات مطابقة.
                  </td>
                </tr>
              )}
              {filtered.map((r) => {
                const phone = r.user?.phone?.replace(/[^0-9]/g, '') || '';
                const msg = encodeURIComponent(`📚 الرجاء إرجاع الكتاب "${r.book?.name}" قبل ${fmtDate(r.dueDate)}`);
                return (
                  <tr key={r._id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">{r.user?.name || '—'}</td>
                    <td className="px-4 py-3">{r.book?.name || '—'}</td>
                    <td className={`px-4 py-3 ${getBg(r.dueDate)} rounded-lg`}>
                      {fmtDate(r.dueDate)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {r.notificationSent ? '✅' : '❌'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {phone ? (
                        <a
                          href={`https://wa.me/${phone}?text=${msg}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block px-4 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-sm transition"
                        >
                          إرسال
                        </a>
                      ) : (
                        <span className="text-gray-400 italic">لا يوجد رقم</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
}
